import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { AlertCircle, Download, Loader2, Plus, Receipt, Search, Upload } from 'lucide-react';
import { bauvorhabenApi, gewerkeApi, invoicesApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { useAppEvent, emitAppEvent } from '@/utils/appEvents';
import { amountsMatch, deriveNetTax, paidToggleAction, type GridField } from './cellEdit';
import { RechnungenGrid, type InvoiceDraft, type SortValue } from './RechnungenGrid';
import { InvoiceImportModal } from './InvoiceImportModal';
import type { Invoice, InvoiceStatus, InvoiceWritePayload } from '@/types';

/**
 * Vollbreite Rechnungen-Seite ("verlinkte Excel"): besitzt Filter, Daten,
 * Draft-Zeilen und die Autosave-Mechanik (per Row serialisierte PUTs, Merge
 * der zurückgegebenen Row). Der Detail-Drawer hängt als Outlet-Kind an
 * /rechnungen/:id — das Grid bleibt gemountet, Filter/Scroll überleben.
 */

const todayIso = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const cellKey = (id: number, field: GridField) => `${id}:${field}`;

export function RechnungenPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  // ---------- Filter ----------
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [directionFilter, setDirectionFilter] = useState<'' | 'eingang' | 'ausgang'>('eingang');
  const [statusFilter, setStatusFilter] = useState<'' | InvoiceStatus>('');
  const [bauvorhabenFilter, setBauvorhabenFilter] = useState('');
  const [gewerkFilter, setGewerkFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sort, setSort] = useState<SortValue>('invoice_date_desc');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filterParams = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      direction: directionFilter || undefined,
      status: statusFilter || undefined,
      bauvorhaben_id: bauvorhabenFilter || undefined,
      gewerk_id: gewerkFilter || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      sort,
    }),
    [debouncedSearch, directionFilter, statusFilter, bauvorhabenFilter, gewerkFilter, dateFrom, dateTo, sort]
  );

  // ---------- Daten ----------
  const [rows, setRows] = useState<Invoice[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const loadSeq = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const seq = ++loadSeq.current;
    setIsLoading(true);
    setError('');
    invoicesApi
      .getAll({ page: 1, ...filterParams })
      .then((response) => {
        if (cancelled || seq !== loadSeq.current) return;
        setRows(response.data.data);
        const pagination = response.data.pagination;
        setHasMore(!!pagination && pagination.page < pagination.total_pages);
        setPage(1);
      })
      .catch(() => {
        if (!cancelled) setError('Fehler beim Laden der Rechnungen');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filterParams]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    invoicesApi
      .getAll({ page: nextPage, ...filterParams })
      .then((response) => {
        setRows((prev) => [...prev, ...response.data.data]);
        const pagination = response.data.pagination;
        setHasMore(!!pagination && pagination.page < pagination.total_pages);
        setPage(nextPage);
      })
      .catch(() => setError('Fehler beim Laden der Rechnungen'))
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, hasMore, page, filterParams]);

  // Stilles Nachladen der Seiten 1..page (Page-Count bleibt erhalten).
  const silentReload = useCallback(() => {
    const seq = ++loadSeq.current;
    const requests = [];
    for (let p = 1; p <= page; p++) {
      requests.push(invoicesApi.getAll({ page: p, ...filterParams }));
    }
    Promise.all(requests)
      .then((responses) => {
        if (seq !== loadSeq.current) return;
        setRows(responses.flatMap((r) => r.data.data));
        const last = responses[responses.length - 1];
        const pagination = last.data.pagination;
        setHasMore(!!pagination && pagination.page < pagination.total_pages);
      })
      .catch(() => {
        // Still scheitern — die nächste Aktion lädt ohnehin neu.
      });
  }, [page, filterParams]);
  const silentReloadRef = useRef(silentReload);
  silentReloadRef.current = silentReload;

  useAppEvent('rechnungen:changed', () => silentReloadRef.current());

  // ---------- Dropdown-Daten (BV seitenweise, Gewerke-Katalog) ----------
  const [bauvorhabenOptions, setBauvorhabenOptions] = useState<Array<{ id: number; name: string }>>([]);
  const [gewerkOptions, setGewerkOptions] = useState<Array<{ id: number; name: string }>>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all: Array<{ id: number; name: string }> = [];
      // per_page-Cap 100 — seitenweise bis total_pages (Plan-Risiko 3).
      for (let p = 1; p <= 20; p++) {
        const response = await bauvorhabenApi.getAll({ page: p, per_page: 100, sort: 'name_asc' });
        all.push(...response.data.data.map((b) => ({ id: b.id, name: b.name })));
        const totalPages = response.data.pagination?.total_pages ?? p;
        if (p >= totalPages) break;
      }
      if (!cancelled) setBauvorhabenOptions(all);
    })().catch(() => {
      if (!cancelled) setBauvorhabenOptions([]);
    });
    gewerkeApi
      .getAll()
      .then((response) =>
        setGewerkOptions(
          response.data.data.phases.flatMap((phase) => phase.gewerke.map((g) => ({ id: g.id, name: g.name })))
        )
      )
      .catch(() => setGewerkOptions([]));
    return () => {
      cancelled = true;
    };
  }, []);

  // ---------- Zellen-Autosave (per Row serialisiert) ----------
  const [cellSaving, setCellSaving] = useState<Set<string>>(new Set());
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map());
  const rowQueues = useRef(new Map<number, Promise<void>>());

  const markSaving = (id: number, fields: GridField[], on: boolean) => {
    setCellSaving((prev) => {
      const next = new Set(prev);
      for (const field of fields) {
        const key = cellKey(id, field);
        if (on) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  };

  const setCellError = (id: number, fields: GridField[], message: string) => {
    setCellErrors((prev) => {
      const next = new Map(prev);
      for (const field of fields) {
        const key = cellKey(id, field);
        if (message) next.set(key, message);
        else next.delete(key);
      }
      return next;
    });
  };

  /**
   * Ein Zellen-Commit → PUT mit einem oder mehreren Feldern (MwSt-Ableitung
   * sendet Netto+Satz+Steuer gemeinsam). Requests derselben Zeile werden
   * serialisiert; bei Erfolg wird die zurückgegebene Row gemergt (frischt
   * Join-Namen + Server-Werte auf).
   */
  const saveCell = useCallback((id: number, payload: Record<string, unknown>, fields: GridField[]) => {
    setCellError(id, fields, '');
    markSaving(id, fields, true);

    const previous = rowQueues.current.get(id) ?? Promise.resolve();
    const next = previous.then(async () => {
      try {
        const response = await invoicesApi.update(id, payload as InvoiceWritePayload);
        const saved = response.data.data;
        setRows((prevRows) => prevRows.map((r) => (r.id === id ? { ...r, ...saved } : r)));
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Speichern';
        setCellError(id, fields, message);
        // Veraltetes UI (Status hat sich zwischenzeitlich geändert): Zeilen
        // frisch laden, damit kein Wedged-Zustand bleibt.
        silentReloadRef.current();
      } finally {
        markSaving(id, fields, false);
      }
    });
    rowQueues.current.set(id, next);
  }, []);

  /** Ein Zellen-Commit auf ein Betragsfeld: MwSt-Ableitung je Feld. */
  const handleSaveCell = useCallback(
    (id: number, payload: Record<string, unknown>, fields: GridField[]) => {
      const row = rows.find((r) => r.id === id);
      if (!row) return;

      // Client-Prechecks (gleiche Regeln wie validateAndNormalize) — kein
      // PUT, wenn es der Server ohnehin ablehnen würde.
      if (payload.due_date != null) {
        const invoiceDate = row.invoice_date;
        if (invoiceDate && String(payload.due_date) < invoiceDate) {
          setCellError(id, fields, 'Fälligkeit liegt vor dem Rechnungsdatum');
          return;
        }
      }
      if (payload.amount_net != null && payload.amount_tax == null) {
        const gross = row.amount_gross != null ? Number(row.amount_gross) : null;
        const tax = row.amount_tax != null ? Number(row.amount_tax) : null;
        if (gross != null && tax != null && !amountsMatch(Number(payload.amount_net), tax, gross)) {
          setCellError(id, fields, 'Netto + MwSt ergibt nicht das Brutto');
          return;
        }
      }

      // MwSt-Satz geändert → Netto/Steuer aus dem Brutto ableiten (ein PUT).
      if (payload.tax_rate_percent != null) {
        const gross = Number(row.amount_gross);
        const derived = deriveNetTax(gross, Number(payload.tax_rate_percent));
        if (derived) {
          saveCell(
            id,
            { tax_rate_percent: Number(payload.tax_rate_percent), amount_net: derived.net, amount_tax: derived.tax },
            ['tax_rate_percent', 'amount_net', 'amount_tax']
          );
          return;
        }
      }
      // Brutto geändert → Netto/Steuer aus dem bestehenden Satz ableiten.
      if (payload.amount_gross != null && row.tax_rate_percent != null) {
        const derived = deriveNetTax(Number(payload.amount_gross), Number(row.tax_rate_percent));
        if (derived) {
          saveCell(
            id,
            { amount_gross: Number(payload.amount_gross), amount_net: derived.net, amount_tax: derived.tax },
            ['amount_gross', 'amount_net', 'amount_tax']
          );
          return;
        }
      }
      saveCell(id, payload, fields);
    },
    [rows, saveCell]
  );

  const handleUploadFile = useCallback((id: number, file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      setCellError(id, ['file'], 'Datei ist zu groß (maximal 10 MB)');
      return;
    }
    markSaving(id, ['file'], true);
    setCellError(id, ['file'], '');
    invoicesApi
      .uploadFile(id, file)
      .then((response) => {
        const saved = response.data.data;
        setRows((prevRows) =>
          prevRows.map((r) =>
            r.id === id
              ? {
                  ...r,
                  has_file: true,
                  file_path: saved.file_path,
                  file_original_name: saved.file_original_name,
                  file_size: saved.file_size,
                  file_mime: saved.file_mime,
                }
              : r
          )
        );
        emitAppEvent('rechnungen:changed');
      })
      .catch((err) => {
        setCellError(id, ['file'], (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Hochladen');
      })
      .finally(() => markSaving(id, ['file'], false));
  }, []);

  const handleTogglePaid = useCallback((id: number, paid: boolean) => {
    markSaving(id, ['payment'], true);
    invoicesApi
      .transition(id, paidToggleAction(paid))
      .then(() => {
        emitAppEvent('rechnungen:changed');
        silentReloadRef.current();
      })
      .catch((err) => {
        setCellError(id, ['payment'], (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Statuswechsel');
      })
      .finally(() => markSaving(id, ['payment'], false));
  }, []);

  const handleDelete = useCallback((id: number) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) return;
    invoicesApi
      .remove(id)
      .then(() => {
        emitAppEvent('rechnungen:changed');
        silentReloadRef.current();
      })
      .catch((err) => setError((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Löschen'));
  }, []);

  // ---------- Draft-Zeilen (neue Rechnungen) ----------
  const [drafts, setDrafts] = useState<InvoiceDraft[]>([]);
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;
  const draftSeq = useRef(0);

  const commitDraft = useCallback((draft: InvoiceDraft) => {
    setDrafts((prev) => prev.map((d) => (d.key === draft.key ? { ...d, isCommitting: true, commitError: '' } : d)));
    invoicesApi
      .create({
        direction: draft.direction,
        invoice_number: draft.invoice_number.trim(),
        contact_id: draft.contact_id,
        bauvorhaben_id: draft.bauvorhaben_id,
        gewerk_id: draft.gewerk_id,
        amount_net: draft.amount_net.trim() === '' ? null : Number(draft.amount_net.replace(',', '.')),
        amount_tax: draft.amount_tax.trim() === '' ? null : Number(draft.amount_tax.replace(',', '.')),
        amount_gross: Number(draft.amount_gross.replace(',', '.')),
        tax_rate_percent: draft.tax_rate_percent.trim() === '' ? null : Number(draft.tax_rate_percent.replace(',', '.')),
        invoice_date: draft.invoice_date,
        due_date: draft.due_date || null,
        description: draft.description.trim() || null,
        notes: null,
      })
      .then((response) => {
        const newId = (response.data.data as { id: number }).id;
        const file = draft.pendingFile;
        setDrafts((prev) => prev.filter((d) => d.key !== draft.key));
        emitAppEvent('rechnungen:changed');
        if (file) {
          return invoicesApi
            .uploadFile(newId, file)
            .then(() => emitAppEvent('rechnungen:changed'))
            .catch(() => {
              // PDF nachreichen über die Zelle — die Rechnung existiert bereits.
              setError('Rechnung erstellt, aber der PDF-Upload ist fehlgeschlagen. Bitte in der Zeile erneut hochladen.');
            });
        }
        return undefined;
      })
      .catch((err) => {
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Fehler beim Anlegen';
        setDrafts((prev) => prev.map((d) => (d.key === draft.key ? { ...d, isCommitting: false, commitError: message } : d)));
      });
  }, []);

  const changeDraft = (key: string, patch: Partial<InvoiceDraft>) => {
    const current = draftsRef.current.find((d) => d.key === key);
    if (!current) return;
    const next: InvoiceDraft = { ...current, ...patch };

    // MwSt-Ableitung je geändertem Feld (wie im Grid).
    if (patch.tax_rate_percent !== undefined || patch.amount_gross !== undefined) {
      const rate = parseFloat(next.tax_rate_percent.replace(',', '.'));
      const gross = parseFloat(next.amount_gross.replace(',', '.'));
      const derived = Number.isFinite(rate) && Number.isFinite(gross) ? deriveNetTax(gross, rate) : null;
      if (derived) {
        next.amount_net = String(derived.net);
        next.amount_tax = String(derived.tax);
      }
    }

    setDrafts((prev) => prev.map((d) => (d.key === key ? next : d)));

    // Auto-Commit, sobald die Pflichtfelder (Nummer, Brutto > 0, Datum) valid
    // sind — das Autosave-Modell: die Zeile "speichert sich selbst".
    const gross = parseFloat(next.amount_gross.replace(',', '.'));
    const isValid =
      next.invoice_number.trim() !== '' &&
      Number.isFinite(gross) &&
      gross > 0 &&
      /^\d{4}-\d{2}-\d{2}$/.test(next.invoice_date);
    if (isValid && !next.isCommitting) commitDraft(next);
  };

  const addDraft = () => {
    const key = `draft-${++draftSeq.current}`;
    setDrafts((prev) => [
      ...prev,
      {
        key,
        direction: directionFilter === 'ausgang' ? 'ausgang' : 'eingang',
        invoice_number: '',
        contact_id: null,
        contact_name: null,
        bauvorhaben_id: null,
        gewerk_id: null,
        amount_net: '',
        amount_tax: '',
        amount_gross: '',
        tax_rate_percent: '',
        invoice_date: todayIso(),
        due_date: '',
        description: '',
        pendingFile: null,
        isCommitting: false,
        commitError: '',
      },
    ]);
  };

  const cancelDraft = (key: string) => {
    setDrafts((prev) => prev.filter((d) => d.key !== key));
  };

  // ---------- Toolbar-Aktionen ----------
  const [isExporting, setIsExporting] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const handleExport = () => {
    setIsExporting(true);
    invoicesApi
      .exportCsv({ direction: directionFilter || undefined, status: statusFilter || undefined })
      .then((response) => {
        const { filename, content_base64 } = response.data.data;
        const binary = atob(content_base64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => setError('Fehler beim Exportieren'))
      .finally(() => setIsExporting(false));
  };

  const resetFilters = () => {
    setSearchTerm('');
    setDirectionFilter('eingang');
    setStatusFilter('');
    setBauvorhabenFilter('');
    setGewerkFilter('');
    setDateFrom('');
    setDateTo('');
    setSort('invoice_date_desc');
  };

  const hasActiveFilters =
    debouncedSearch !== '' || directionFilter !== '' || statusFilter !== '' || bauvorhabenFilter !== '' ||
    gewerkFilter !== '' || dateFrom !== '' || dateTo !== '';

  // Zusammengefasster Zellenfehler (erste fehlerhafte Zelle) als Hinweisband.
  const firstCellError = Array.from(cellErrors.values())[0];

  return (
    <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
      {/* Toolbar */}
      <div className="sticky top-0 z-20 space-y-2 border-b border-gray-200 bg-gray-50/95 px-4 py-3 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-gray-600" />
            <h1 className="text-lg font-bold text-gray-900">Rechnungen</h1>
          </div>
          {isAdmin && (
            <div className="flex gap-2">
              <button
                onClick={handleExport}
                disabled={isExporting}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                title="CSV-Export für Lexware"
              >
                <Download className="h-4 w-4" />
                {isExporting ? 'Export…' : 'Export'}
              </button>
              <button
                onClick={() => setIsImportOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                title="Excel-/CSV-Liste importieren (max. 200 Zeilen pro Durchlauf)"
              >
                <Upload className="h-4 w-4" />
                Importieren
              </button>
              <button onClick={addDraft} className="btn btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm">
                <Plus className="h-4 w-4" />
                Neu
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suchen — Nummer, Betreff, Lieferant…"
              className="input py-1.5 pl-9 text-sm"
            />
          </div>
          <div className="flex gap-1">
            {([['', 'Alle'], ['eingang', 'Eingang'], ['ausgang', 'Ausgang']] as const).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setDirectionFilter(value)}
                className={`rounded-full border px-3 py-1 text-sm transition-colors ${
                  directionFilter === value
                    ? 'border-primary-600 bg-primary-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | InvoiceStatus)}
            className="input w-auto py-1.5 text-sm"
          >
            <option value="">Alle Status</option>
            <option value="erfasst">Erfasst</option>
            <option value="formal_geprueft">Formal geprüft</option>
            <option value="freigegeben">Freigegeben</option>
            <option value="korrektur_erforderlich">Korrektur erforderlich</option>
            <option value="abgelehnt">Abgelehnt</option>
            <option value="an_lexware_uebergeben">An Lexware übergeben</option>
          </select>
          <select
            value={bauvorhabenFilter}
            onChange={(e) => setBauvorhabenFilter(e.target.value)}
            className="input w-auto max-w-[220px] py-1.5 text-sm"
          >
            <option value="">Alle Bauvorhaben</option>
            {bauvorhabenOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <select
            value={gewerkFilter}
            onChange={(e) => setGewerkFilter(e.target.value)}
            className="input w-auto py-1.5 text-sm"
          >
            <option value="">Alle Gewerke</option>
            {gewerkOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="input w-auto py-1.5 text-sm"
            title="Datum von"
          />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="input w-auto py-1.5 text-sm"
            title="Datum bis"
          />
          {hasActiveFilters && (
            <button onClick={resetFilters} className="text-sm text-gray-500 underline-offset-2 hover:text-gray-800 hover:underline">
              Filter zurücksetzen
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {firstCellError && (
        <div className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {firstCellError} — die betroffene Zelle ist rot markiert; eine neue Bearbeitung räumt den Fehler auf.
        </div>
      )}
      {drafts.some((d) => d.commitError) && (
        <div className="mx-4 mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {drafts
            .filter((d) => d.commitError)
            .map((d) => (
              <p key={d.key}>
                {d.invoice_number ? `${d.invoice_number}: ` : ''}
                {d.commitError}
              </p>
            ))}
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : (
        <RechnungenGrid
          rows={rows}
          drafts={drafts}
          bauvorhabenOptions={bauvorhabenOptions}
          gewerkOptions={gewerkOptions}
          isAdmin={isAdmin}
          sort={sort}
          onSortChange={setSort}
          onSaveCell={handleSaveCell}
          onOpenDetail={(id) => navigate(`/rechnungen/${id}`)}
          onUploadFile={handleUploadFile}
          onTogglePaid={handleTogglePaid}
          onDelete={handleDelete}
          onDraftChange={changeDraft}
          onCancelDraft={cancelDraft}
          cellSaving={cellSaving}
          cellErrors={cellErrors}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={loadMore}
        />
      )}

      {/* Excel-/CSV-Import (Modal) */}
      {isImportOpen && <InvoiceImportModal onClose={() => setIsImportOpen(false)} />}

      {/* Detail-Drawer als Outlet-Kind (öffnet bei /rechnungen/:id) */}
      <Outlet />
    </div>
  );
}